/* sb-dc-tplboard.js  (2026-09-09)
 *
 * Problem: when the design center is opened with ?tpl=<id> the app bundle
 * builds the template's SVG against the DEFAULT board (96x48 in) and then
 * "refits" that result onto the real board. On a retractable / x-banner board
 * (31.5 x 80 in) the refit squashes everything into a small blob in the middle
 * of the board. A second, milder failure happens when the bundle does build on
 * the right board: it fits by HEIGHT, so a 3024x7315 template spills about 3 in
 * off each side of a 31.5 in wide board.
 *
 * Fix: once the board has settled on the product preset, rebuild the layers
 * with the app's OWN converter (window.__dcSvgToLayers) called with a board
 * that keeps the SVG aspect ratio (contain fit), center the result, and write
 * it back with setDoc. The refit baseline (dcRefitBase / dcRefitLast) is
 * rewritten to the real board so the bundle's refit pass becomes a no-op and
 * cannot squash it again. We stop as soon as the doc stays as we laid it out,
 * so a layer the customer moves is never pulled back.
 *
 * Only runs when ?tpl= is present. Diagnostics: window.__dcTplBoard().
 */
(function () {
  'use strict';

  var q;
  try { q = new URLSearchParams(location.search); } catch (e) { return; }
  var TPL = q.get('tpl') || q.get('template') || '';
  if (!TPL) return;

  var info = { tpl: TPL, state: 'waiting', tries: 0, fixes: 0 };
  window.__dcTplBoard = function () { return info; };

  var MAX_TRIES = 60;      // 60 x 400ms = 24s
  var svgText = null;
  var svgUrl = null;
  var busy = false;
  var mine = null;         // geometry of the layout we wrote, as a string
  var stable = 0;
  var MAX_FIXES = 8;
  var lastBoard = '';      // board seen on the previous tick
  var boardHeld = 0;       // ticks the board has been unchanged
  var MIN_HOLD = 3;        // wait ~1.2s for the product preset to land

  function api() { return window.__dcApi; }

  function board() {
    var a = api();
    if (!a || !a.spec) return null;
    var w = +a.spec.widthIn, h = +a.spec.heightIn;
    if (!(w > 0) || !(h > 0)) return null;
    return { w: w, h: h };
  }

  function geom(list) {
    return (list || []).map(function (l) {
      return [l.id, l.xIn, l.yIn, l.widthIn, l.heightIn].join(',');
    }).join('|');
  }

  // true while the doc on screen is not the one we laid out
  function needsFix() {
    var a = api();
    if (!a || !a.front) return false;
    return mine === null || geom(a.front.layers) !== mine;
  }

  function fetchUrl() {
    if (svgUrl) return Promise.resolve(svgUrl);
    var S = window.__dcSupabase;
    if (!S || !S.url || !S.anonKey) return Promise.resolve(null);
    var u = S.url + '/rest/v1/dc_templates?id=eq.' +
            encodeURIComponent(TPL) + '&select=file_url';
    return fetch(u, { headers: { apikey: S.anonKey, Authorization: 'Bearer ' + S.anonKey } })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        svgUrl = (j && j[0] && j[0].file_url) || null;
        return svgUrl;
      })
      .catch(function () { return null; });
  }

  function fetchSvg() {
    if (svgText != null) return Promise.resolve(svgText);
    return fetchUrl().then(function (u) {
      if (!u || !/\.svg(\?|$)/i.test(u)) { info.state = 'not-svg'; return null; }
      return fetch(u).then(function (r) { return r.text(); })
        .then(function (t) { svgText = t; return t; })
        .catch(function () { return null; });
    });
  }

  function viewBox(txt) {
    var m = txt && txt.match(/viewBox\s*=\s*"([^"]+)"/i);
    if (!m) return null;
    var p = m[1].trim().split(/[\s,]+/).map(Number);
    if (p.length < 4 || !(p[2] > 0) || !(p[3] > 0)) return null;
    return { w: p[2], h: p[3] };
  }

  function snapshot(list) {
    return list.map(function (l) {
      return {
        id: l.id, type: l.type, shape: l.shape, visible: l.visible,
        xIn: l.xIn, yIn: l.yIn, widthIn: l.widthIn, heightIn: l.heightIn,
        cornerRadiusIn: l.cornerRadiusIn, fontSizeIn: l.fontSizeIn,
        strokeWidthIn: l.strokeWidthIn, fitMaxIn: l.fitMaxIn
      };
    });
  }

  function apply() {
    if(window.SBDC914?.host.handled(window.__dcApi?.doc)) { info.state = 'ok'; return Promise.resolve(true); }
    var a = api();
    if (!a || !a.front || typeof a.setDoc !== 'function') return Promise.resolve(false);
    if (typeof window.__dcSvgToLayers !== 'function') return Promise.resolve(false);
    var b = board();
    if (!b) return Promise.resolve(false);
    if (!(a.front.layers || []).length) return Promise.resolve(false);

    // the product preset (e.g. 31.5 x 80 for a retractable) lands a moment
    // after the app boots on its default 96 x 48 board. Never lay out against
    // a board that is still moving.
    var key = b.w + 'x' + b.h;
    if (key !== lastBoard) {
      lastBoard = key;
      boardHeld = 0;
      mine = null;               // a new board needs a new layout
      stable = 0;
      info.state = 'board-moving';
      return Promise.resolve(false);
    }
    boardHeld++;
    if (boardHeld < MIN_HOLD) { info.state = 'settling'; return Promise.resolve(false); }

    if (!needsFix()) { info.state = 'ok'; stable++; return Promise.resolve(true); }
    if (info.fixes > 0 && mine !== null) { info.state = 'customer-edited'; clearInterval(timer); return Promise.resolve(true); }
    stable = 0;
    if (info.fixes >= MAX_FIXES) { info.state = 'gave-up'; return Promise.resolve(true); }

    return fetchSvg().then(function (txt) {
      if (!txt) return true;                       // nothing we can do
      var vb = viewBox(txt);
      if (!vb) { info.state = 'no-viewbox'; return true; }

      var s = Math.min(b.w / vb.w, b.h / vb.h);
      var cw = vb.w * s, ch = vb.h * s;
      var dx = (b.w - cw) / 2, dy = (b.h - ch) / 2;

      return Promise.resolve(window.__dcSvgToLayers(txt, cw, ch)).then(function (built) {
        var out = (built && (built.layers || built)) || [];
        if (!out.length) { info.state = 'no-layers'; return true; }
        var lay = out.map(function (l) {
          var c = {};
          for (var k in l) if (Object.prototype.hasOwnProperty.call(l, k)) c[k] = l[k];
          c.xIn = l.xIn + dx;
          c.yIn = l.yIn + dy;
          return c;
        });
        api().setDoc({
          background: (built && built.background) || a.front.background,
          layers: lay,
          viewingDistanceFt: a.front.viewingDistanceFt,
          dcRefitBase: { w: b.w, h: b.h, layers: snapshot(lay) },
          dcRefitLast: { w: b.w, h: b.h, boxes: [] }
        });
        info.fixes++;
        info.state = 'fixed';
        info.board = b;
        info.scale = s;
        info.count = lay.length;
        // let the app settle (text metrics get re-measured), then remember
        // what is on screen so we do not rewrite the same layout again.
        return new Promise(function (res) {
          setTimeout(function () {
            mine = geom(api().front.layers);
            res(true);
          }, 600);
        });
      });
    }).catch(function (e) {
      info.state = 'error';
      info.error = String(e && e.message || e);
      return true;
    });
  }

  function tick() {
    if (busy) return;
    info.tries++;
    if (info.tries > MAX_TRIES) { clearInterval(timer); return; }
    busy = true;
    apply().then(function (done) {
      busy = false;
      // once the doc has stayed as we laid it out the bundle is done
      if (done && info.fixes > 0 && stable >= 5 && info.tries > 15) clearInterval(timer);
    }, function () { busy = false; });
  }

  var timer = setInterval(tick, 400);
  tick();
})();
